/*
  SIGNATURE CHAT OFFSET

  Keeps the MessageMind chat button above the theme's bottom bars: the Signature Sticky Add to Cart (PDP) and the
  floating filter button (collection, mobile). The widget renders in an open shadow root with an inline
  `bottom: 20px`, which theme CSS cannot reach, so one rule is injected into that shadow root: it reads
  --signature-chat-bottom, which this script sets on <html> from the bars actually on screen. A bar only counts when it
  is visible and shares columns with the chat button; otherwise the widget keeps its own 20px.

  The same rule caps the widget's inline z-index (2147483647) at 998, one below the theme's drawers and popovers (999:
  cart, search, menu, filters, quick buy), so it slides under them instead of floating on top.
*/
(() => {
  const DEFAULT_BOTTOM = 20;
  const GAP = 12;
  const CHAT_SELECTOR = '.messagemindanimated-btn';
  const OBSTACLES = ['.signature-sticky-atc.is-visible', 'facet-floating-filter .button'];
  const STYLE_ID = 'signature-chat-offset';
  const RULE = `.messagemindanimated-btn, .messageMind__Call__Min__Main__Wrapper {
    bottom: var(--signature-chat-bottom, ${DEFAULT_BOTTOM}px) !important;
    z-index: 998 !important;
    transition: bottom 0.2s ease;
  }`;

  let shadowRoot = null;
  let frame = null;

  const findShadowRoot = () => {
    for (const element of document.querySelectorAll('body *')) {
      if (element.shadowRoot && element.shadowRoot.querySelector(CHAT_SELECTOR)) {
        return element.shadowRoot;
      }
    }

    return null;
  };

  const injectRule = () => {
    if (!shadowRoot || shadowRoot.getElementById(STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = RULE;
    shadowRoot.appendChild(style);
  };

  const isShown = (element) => {
    const style = getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none' && parseFloat(style.opacity) > 0.1;
  };

  const update = () => {
    frame = null;

    const chat = shadowRoot?.querySelector(CHAT_SELECTOR);

    if (!chat) {
      return;
    }

    injectRule();

    const chatRect = chat.getBoundingClientRect();
    let bottom = DEFAULT_BOTTOM;

    document.querySelectorAll(OBSTACLES.join(',')).forEach((obstacle) => {
      const host = obstacle.closest('facet-floating-filter') || obstacle;
      const rect = obstacle.getBoundingClientRect();

      if (!isShown(host) || rect.height === 0 || rect.top >= window.innerHeight) {
        return;
      }

      if (rect.right > chatRect.left && rect.left < chatRect.right) {
        bottom = Math.max(bottom, Math.ceil(window.innerHeight - rect.top + GAP));
      }
    });

    document.documentElement.style.setProperty('--signature-chat-bottom', `${bottom}px`);
  };

  const scheduleUpdate = () => {
    if (!frame) {
      frame = requestAnimationFrame(update);
    }
  };

  const start = () => {
    shadowRoot = findShadowRoot();

    if (!shadowRoot) {
      return false;
    }

    injectRule();
    scheduleUpdate();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    // The sticky bar shows and hides through its is-visible class, the floating filter through inline styles.
    const obstacleObserver = new MutationObserver(scheduleUpdate);
    document.querySelectorAll('.signature-sticky-atc, facet-floating-filter').forEach((element) => {
      obstacleObserver.observe(element, { attributes: true, attributeFilter: ['class', 'style'] });
    });

    return true;
  };

  if (!start()) {
    // The widget script loads late and fills its shadow root on its own schedule, which a light DOM observer cannot
    // see, so poll for a while instead.
    let attempts = 0;
    const poll = setInterval(() => {
      if (start() || ++attempts >= 60) {
        clearInterval(poll);
      }
    }, 500);
  }
})();
